<template>
  <div
    v-if="hasStatus"
    class="message-status-pill-wrapper"
  >
    <v-tooltip
      bottom
      color="secondary"
      :open-delay="100"
      :max-width="320"
    >
      <template v-slot:activator="{ on }">
        <v-chip
          v-on="on"
          :color="chipColor"
          :dark="chipDark"
          :outlined="chipOutlined"
          :class="[
            'message-status-pill',
            dense ? 'message-status-pill--dense' : '',
            isSending ? 'message-status-pill--sending' : '',
          ]"
          small
        >
          <v-progress-circular
            v-if="isSending"
            size="14"
            width="2"
            :color="spinnerColor"
            indeterminate
            class="mr-2"
          />
          <span class="message-status-pill__label">
            {{ chipLabel }}
          </span>
        </v-chip>
      </template>
      <div class="message-status-pill__tooltip">
        <div v-if="statusDetail" class="message-status-pill__detail">
          {{ statusDetail }}
        </div>
        <div v-else class="message-status-pill__detail">
          {{ fallbackDetail }}
        </div>
        <div
          v-if="hasVerifiedAt"
          class="message-status-pill__verified-at mt-2"
        >
          <v-icon
            size="16"
            class="mr-1"
            color="success"
          >
            check_circle
          </v-icon>
          {{ verifiedAtLabel }}
        </div>
      </div>
    </v-tooltip>
  </div>
</template>

<script>
import { mapState } from "vuex"
import Helpers from "@/helpers.js"

const STATUS_CONFIG = {
  sending: {
    label: "Sending",
    chipColor: "grey lighten-3",
    chipDark: false,
    chipOutlined: true,
    icon: null,
    spinnerColor: "grey darken-2",
  },
  sent: {
    label: "Sent",
    chipColor: "primary",
    chipDark: true,
    chipOutlined: false,
    icon: "send",
  },
  verified: {
    label: "Verified",
    chipColor: "success",
    chipDark: true,
    chipOutlined: false,
    icon: "check_circle",
  },
  verification_failed: {
    label: "Verification Failed",
    chipColor: "amber darken-3",
    chipDark: true,
    chipOutlined: false,
    icon: "error_outline",
  },
  failed: {
    label: "Failed",
    chipColor: "error",
    chipDark: true,
    chipOutlined: false,
    icon: "report_problem",
  },
}

export default {
  name: "MessageSendStatusPill",
  props: {
    status: {
      type: String,
      default: undefined,
    },
    detail: {
      type: String,
      default: "",
    },
    verifiedAt: {
      type: String,
      default: undefined,
    },
    updatedAt: {
      type: String,
      default: undefined,
    },
    dense: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      userPreferences: (state) => state.currentUser.me.preferences,
    }),
    hasStatus() {
      // Only show badge for failed states, not for "sent" status
      if (!this.status) {
        return false
      }
      // Only show for failed states
      return this.status === "failed" || this.status === "verification_failed"
    },
    statusConfig() {
      return STATUS_CONFIG[this.status] || null
    },
    isSending() {
      return this.status === "sending"
    },
    chipLabel() {
      if (this.statusConfig) {
        return this.statusConfig.label
      }
      return this.status
        ? this.status
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase())
        : ""
    },
    chipColor() {
      return this.statusConfig ? this.statusConfig.chipColor : "grey lighten-2"
    },
    chipDark() {
      return this.statusConfig ? !!this.statusConfig.chipDark : false
    },
    chipOutlined() {
      return this.statusConfig ? !!this.statusConfig.chipOutlined : false
    },
    chipIcon() {
      return this.statusConfig ? this.statusConfig.icon : null
    },
    spinnerColor() {
      if (this.statusConfig && this.statusConfig.spinnerColor) {
        return this.statusConfig.spinnerColor
      }
      return this.chipDark ? "white" : "grey darken-2"
    },
    statusDetail() {
      return this.detail && this.detail.trim().length > 0 ? this.detail.trim() : ""
    },
    fallbackDetail() {
      if (this.isSending) {
        return "Waiting for the mail server to confirm your message."
      }
      if (this.status === "sent") {
        return "Message accepted by the outbound mail server."
      }
      if (this.status === "verified") {
        return "Message located in the Sent mailbox."
      }
      if (this.status === "verification_failed") {
        return "We could not confirm the message in the Sent mailbox."
      }
      if (this.status === "failed") {
        return "Message delivery failed."
      }
      return "Status updated."
    },
    hasVerifiedAt() {
      return !!this.verifiedAt
    },
    verifiedAtLabel() {
      if (!this.hasVerifiedAt) {
        return ""
      }
      try {
        const formatted = Helpers.formatDate(
          this.verifiedAt,
          this.userPreferences && this.userPreferences.dateTimeFormat
            ? this.userPreferences.dateTimeFormat
            : "Do MMM YYYY HH:mm"
        )
        return `Verified ${formatted}`
      } catch (err) {
        return `Verified ${new Date(this.verifiedAt).toLocaleString()}`
      }
    },
  },
}
</script>

<style scoped>
.message-status-pill-wrapper {
  display: inline-flex;
  align-items: center;
}

.message-status-pill {
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
}

.message-status-pill--dense {
  height: 24px;
}

.message-status-pill--sending {
  min-width: 110px;
}

.message-status-pill__label {
  display: inline-flex;
  align-items: center;
}

.message-status-pill__tooltip {
  max-width: 280px;
  white-space: normal;
}

.message-status-pill__detail {
  white-space: pre-wrap;
  line-height: 1.4;
}

.message-status-pill__verified-at {
  display: flex;
  align-items: center;
  font-weight: 500;
}
</style>

